/**
 * Content Notification Service - Lambda Version
 * Secure access to newsletter subscribers and notification tracking
 * Email sending still uses email Lambda service
 * No AWS credentials needed!
 */

const DYNAMODB_SERVICE_URL = process.env.REACT_APP_DYNAMODB_SERVICE_URL;
const EMAIL_SERVICE_URL = process.env.REACT_APP_EMAIL_SERVICE_URL;

/**
 * Get all verified newsletter subscribers
 */
export const getSubscribers = async () => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'get_subscribers'
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch subscribers');
        }

        return data.subscribers || [];
    } catch (error) {
        console.error('Error fetching subscribers:', error);
        throw error;
    }
};

/**
 * Send notification email to a subscriber
 */
const sendNotificationEmail = async (subscriberEmail, contentData) => {
    const { platform, title, url, excerpt, author, publishDate } = contentData;

    try {
        // Use email Lambda service for sending emails
        const response = await fetch(EMAIL_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'content_notification',
                to: subscriberEmail,
                platform,
                title,
                url,
                excerpt,
                author,
                publishDate
            })
        });

        const data = await response.json();
        return { success: data.success, email: subscriberEmail };
    } catch (error) {
        console.error(`Failed to send notification to ${subscriberEmail}:`, error);
        return { success: false, email: subscriberEmail, error: error.message };
    }
};

/**
 * Notify all subscribers about new content
 */
export const notifySubscribers = async (contentData) => {
    try {
        // Get all verified subscribers
        const subscribers = await getSubscribers();
        
        if (subscribers.length === 0) {
            return {
                success: true,
                message: 'No subscribers to notify',
                sent: 0,
                failed: 0,
                total: 0
            };
        }

        // Send emails to all subscribers
        const emailPromises = subscribers.map(subscriber => 
            sendNotificationEmail(subscriber.email, contentData)
        );

        const results = await Promise.allSettled(emailPromises);

        const sent = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
        const failed = results.filter(r => r.status === 'rejected' || !r.value.success).length;

        return {
            success: true,
            message: `Notifications sent to ${sent} subscribers`,
            sent,
            failed,
            total: subscribers.length
        };
    } catch (error) {
        console.error('Error notifying subscribers:', error);
        throw error;
    }
};

/**
 * Save content notification record to DynamoDB
 */
export const saveNotificationRecord = async (contentData) => {
    try {
        const response = await fetch(DYNAMODB_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                operation: 'save_notification',
                platform: contentData.platform,
                title: contentData.title,
                url: contentData.url,
                excerpt: contentData.excerpt,
                publishDate: contentData.publishDate,
                subscribersNotified: contentData.subscribersNotified || 0
            })
        });

        const data = await response.json();
        
        if (!response.ok) {
            throw new Error(data.error || 'Failed to save notification record');
        }

        return { success: true, notificationId: data.notificationId };
    } catch (error) {
        console.error('Error saving notification record:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Manual notification trigger (for admin panel)
 */
export const sendManualNotification = async (contentData) => {
    try {
        // Notify all subscribers
        const notificationResult = await notifySubscribers(contentData);
        
        // Save notification record
        const recordResult = await saveNotificationRecord({
            ...contentData,
            subscribersNotified: notificationResult.sent
        });

        return {
            success: true,
            ...notificationResult,
            recordId: recordResult.notificationId
        };
    } catch (error) {
        console.error('Error sending manual notification:', error);
        throw error;
    }
};
